import React, { useState, useEffect, useCallback } from 'react';

/**
 * SentenceConstructionAdmin — Pattern-based sentence builder.
 * Define patterns using blocks: [pronoun] [verb] [neg_verb] [connector] [object] [adjective]
 * Test sentences to see how the system breaks them down and constructs Darija.
 *
 * DB: sentence_construction_patterns in Grammar_DB_DEV
 */

const BLOCK_TYPES = [
  { key: 'pronoun', label: 'Pronoun', color: 'bg-blue-500', desc: 'I, you, he, she, we, they' },
  { key: 'verb', label: 'Verb', color: 'bg-emerald-500', desc: 'eat, go, want (positive)' },
  { key: 'neg_verb', label: 'Neg Verb', color: 'bg-red-500', desc: "don't/doesn't + verb" },
  { key: 'connector', label: 'Connector', color: 'bg-purple-500', desc: 'because, so, but, and' },
  { key: 'object', label: 'Object', color: 'bg-amber-500', desc: 'food, house, car (nouns)' },
  { key: 'adjective', label: 'Adjective', color: 'bg-pink-500', desc: 'tired, hungry, happy' },
  { key: 'modal', label: 'Modal', color: 'bg-cyan-500', desc: 'can, could, should, want to, need to' },
];

const blockColor = (type) => {
  const b = BLOCK_TYPES.find(t => t.key === type);
  return b ? b.color : 'bg-gray-500';
};

const SentenceConstructionAdmin = ({ API, adminToken, isDarkMode }) => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('patterns'); // patterns | builder
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Pattern builder state
  const [newPattern, setNewPattern] = useState(null);
  const [editPattern, setEditPattern] = useState(null);

  // Sentence tester state
  const [testSentence, setTestSentence] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const dk = isDarkMode;

  const fetchPatterns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/grammar/construction/patterns`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      if (data.success) setPatterns(data.patterns || []);
    } catch (err) {
      console.error('Failed to load patterns:', err);
    }
    setLoading(false);
  }, [API, adminToken]);

  useEffect(() => { fetchPatterns(); }, [fetchPatterns]);

  const handleSave = async (isNew) => {
    const data = isNew ? newPattern : editPattern;
    if (!data || !data.name || !data.pattern.length) return;
    setSaving(true);
    try {
      const url = isNew
        ? `${API}/admin/grammar/construction/patterns`
        : `${API}/admin/grammar/construction/patterns/${data.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (result.success) {
        if (isNew) setNewPattern(null);
        else setEditPattern(null);
        fetchPatterns();
      }
    } catch (err) {
      console.error('Failed to save:', err);
    }
    setSaving(false);
  };

  const handleDelete = (id, name) => {
    setConfirmDialog({
      title: 'Delete Pattern',
      message: `Delete "${name}"?`,
      onConfirm: async () => {
        try {
          await fetch(`${API}/admin/grammar/construction/patterns/${id}`, {
            method: 'DELETE', headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          fetchPatterns();
        } catch (err) { console.error(err); }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleTest = async () => {
    if (!testSentence.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/admin/grammar/construction/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ sentence: testSentence })
      });
      const data = await res.json();
      if (data.success) setTestResult(data);
    } catch (err) {
      console.error('Test failed:', err);
    }
    setTesting(false);
  };

  const addBlock = (patternData, setPatternData, blockType) => {
    setPatternData({ ...patternData, pattern: [...patternData.pattern, blockType] });
  };

  const removeBlock = (patternData, setPatternData, index) => {
    const newBlocks = [...patternData.pattern];
    newBlocks.splice(index, 1);
    setPatternData({ ...patternData, pattern: newBlocks });
  };

  const inputCls = `w-full px-2 py-1.5 rounded border text-sm ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;

  const renderPatternForm = (data, setData, isNew) => (
    <div className={`p-4 rounded-lg border mb-4 ${dk ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
      <h4 className={`font-bold mb-3 ${dk ? 'text-white' : 'text-gray-800'}`}>
        {isNew ? 'Create New Pattern' : `Edit: ${data.name}`}
      </h4>

      {/* Pattern Name */}
      <div className="mb-3">
        <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Pattern Name</label>
        <input type="text" value={data.name} onChange={e => setData({ ...data, name: e.target.value })}
          className={inputCls} placeholder="e.g. pronoun + verb + connector + pronoun + verb" />
      </div>

      {/* Block Selector */}
      <div className="mb-3">
        <label className={`text-xs font-medium mb-1 block ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
          Click blocks to build pattern:
        </label>
        <div className="flex gap-2 flex-wrap">
          {BLOCK_TYPES.map(bt => (
            <button type="button" key={bt.key} onClick={() => addBlock(data, setData, bt.key)}
              className={`px-3 py-1.5 rounded-lg text-xs text-white font-medium ${bt.color} hover:opacity-80`}
              title={bt.desc}>
              + {bt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Current Pattern Blocks */}
      <div className="mb-3">
        <label className={`text-xs font-medium mb-1 block ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
          Pattern Structure:
        </label>
        <div className="flex gap-1 flex-wrap items-center min-h-[36px]">
          {data.pattern.length === 0 ? (
            <span className={`text-xs italic ${dk ? 'text-gray-500' : 'text-gray-400'}`}>Click blocks above to build pattern</span>
          ) : (
            data.pattern.map((block, i) => (
              <React.Fragment key={i}>
                <span className={`px-2.5 py-1 rounded text-xs text-white font-medium ${blockColor(block)} cursor-pointer hover:opacity-70`}
                  onClick={() => removeBlock(data, setData, i)} title="Click to remove">
                  [{block}] x
                </span>
                {i < data.pattern.length - 1 && (
                  <span className={`text-xs ${dk ? 'text-gray-500' : 'text-gray-400'}`}>+</span>
                )}
              </React.Fragment>
            ))
          )}
        </div>
      </div>

      {/* Examples */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>English Example</label>
          <input type="text" value={data.english_example || ''} onChange={e => setData({ ...data, english_example: e.target.value })}
            className={inputCls} placeholder="I eat food" />
        </div>
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>Darija (Latin)</label>
          <input type="text" value={data.darija_example_latin || ''} onChange={e => setData({ ...data, darija_example_latin: e.target.value })}
            className={inputCls} placeholder="kanakol makla" />
        </div>
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Darija (Arabic)</label>
          <input type="text" dir="rtl" value={data.darija_example_arabic || ''} onChange={e => setData({ ...data, darija_example_arabic: e.target.value })}
            className={`${inputCls} text-right`} placeholder="كناكل ماكلة" />
        </div>
      </div>

      {/* Notes */}
      <div className="mb-3">
        <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Notes</label>
        <input type="text" value={data.notes || ''} onChange={e => setData({ ...data, notes: e.target.value })}
          className={inputCls} placeholder="optional notes" />
      </div>

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => isNew ? setNewPattern(null) : setEditPattern(null)}
          className={`px-3 py-1.5 rounded text-sm ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
        <button type="button" onClick={() => handleSave(isNew)} disabled={saving || !data.pattern.length}
          className="px-3 py-1.5 rounded text-sm bg-emerald-600 text-white hover:bg-emerald-700">
          {saving ? 'Saving...' : isNew ? 'Create Pattern' : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`p-6 rounded-lg border ${dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg max-w-sm ${dk ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`font-bold mb-2 ${dk ? 'text-white' : 'text-gray-800'}`}>{confirmDialog.title}</h4>
            <p className={`mb-4 text-sm ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{confirmDialog.message}</p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={confirmDialog.onCancel} className={`px-3 py-1 rounded ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
              <button type="button" onClick={confirmDialog.onConfirm} className="px-3 py-1 rounded text-white bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-bold ${dk ? 'text-white' : 'text-gray-800'}`}>Sentence Builder</h3>
        <span className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>{patterns.length} patterns</span>
      </div>

      {/* How It Works */}
      <div className={`mb-4 p-3 rounded-lg border text-xs ${dk ? 'bg-gray-700/50 border-gray-600 text-gray-300' : 'bg-blue-50 border-blue-200 text-gray-600'}`}>
        <strong className={dk ? 'text-white' : 'text-gray-800'}>How it works:</strong>
        <span className="ml-1">
          Define patterns using blocks. System matches user input and resolves each block from DB:
        </span>
        <div className="inline-flex gap-1 flex-wrap mt-1">
          <span className="px-1.5 py-0.5 rounded bg-blue-500 text-white">Pronoun</span> I, you, he...
          <span className="px-1.5 py-0.5 rounded bg-emerald-500 text-white">Verb</span> verb_conjugations
          <span className="px-1.5 py-0.5 rounded bg-red-500 text-white">Neg Verb</span> negation groups (ma+stem+sh)
          <span className="px-1.5 py-0.5 rounded bg-purple-500 text-white">Connector</span> smart connectors
          <span className="px-1.5 py-0.5 rounded bg-cyan-500 text-white">Modal</span> can, need to, want to...
          <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white">Object</span> translations DB
          <span className="px-1.5 py-0.5 rounded bg-pink-500 text-white">Adjective</span> translations DB
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => setActiveSection('patterns')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${activeSection === 'patterns' ? 'bg-emerald-600 text-white' : dk ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          Patterns
        </button>
        <button type="button" onClick={() => setActiveSection('builder')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${activeSection === 'builder' ? 'bg-emerald-600 text-white' : dk ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          Test Sentences
        </button>
      </div>

      {/* ═══════════ PATTERNS SECTION ═══════════ */}
      {activeSection === 'patterns' && (
        <div>
          {/* Add Pattern Button */}
          <button type="button" onClick={() => setNewPattern({ name: '', pattern: [], english_example: '', darija_example_latin: '', darija_example_arabic: '', notes: '' })}
            className="mb-4 px-4 py-2 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700">
            + Create Pattern
          </button>

          {/* New Pattern Form */}
          {newPattern && renderPatternForm(newPattern, setNewPattern, true)}

          {/* Edit Pattern Form */}
          {editPattern && renderPatternForm(editPattern, setEditPattern, false)}

          {/* Loading */}
          {loading && <div className={`text-center py-8 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>}

          {/* Pattern List */}
          {!loading && patterns.length === 0 && (
            <div className={`text-center py-8 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>No patterns yet. Create your first pattern above.</div>
          )}

          {!loading && patterns.map(p => (
            <div key={p.id} className={`mb-3 p-4 rounded-lg border ${dk ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${dk ? 'text-white' : 'text-gray-800'}`}>{p.name}</span>
                  {!p.enabled && <span className="text-xs text-red-500">(disabled)</span>}
                </div>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setEditPattern({ ...p })}
                    className={`text-xs px-2 py-1 rounded ${dk ? 'text-blue-400 hover:bg-gray-600' : 'text-blue-600 hover:bg-blue-50'}`}>Edit</button>
                  <button type="button" onClick={() => handleDelete(p.id, p.name)}
                    className={`text-xs px-2 py-1 rounded ${dk ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>Delete</button>
                </div>
              </div>

              {/* Pattern blocks */}
              <div className="flex gap-1 flex-wrap items-center mb-2">
                {p.pattern.map((block, i) => (
                  <React.Fragment key={i}>
                    <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${blockColor(block)}`}>
                      [{block}]
                    </span>
                    {i < p.pattern.length - 1 && <span className={`text-xs ${dk ? 'text-gray-500' : 'text-gray-400'}`}>+</span>}
                  </React.Fragment>
                ))}
              </div>

              {/* Example */}
              {p.english_example && (
                <div className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-medium">Example:</span>{' '}
                  <span>{p.english_example}</span>
                  {p.darija_example_latin && <span className={`ml-2 ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>{p.darija_example_latin}</span>}
                  {p.darija_example_arabic && <span className={`ml-2 ${dk ? 'text-amber-400' : 'text-amber-600'}`} dir="rtl">{p.darija_example_arabic}</span>}
                </div>
              )}
              {p.notes && <p className={`text-xs mt-1 italic ${dk ? 'text-gray-500' : 'text-gray-400'}`}>{p.notes}</p>}
            </div>
          ))}
        </div>
      )}

      {/* ═══════════ TEST SECTION ═══════════ */}
      {activeSection === 'builder' && (
        <div>
          <p className={`text-sm mb-3 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
            Type an English sentence to test. The system will try to match it against your patterns and show the breakdown.
          </p>

          <div className="flex gap-2 mb-4">
            <input type="text" value={testSentence} onChange={e => setTestSentence(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleTest()}
              placeholder="e.g. I eat food because I am hungry"
              className={`flex-1 px-3 py-2.5 rounded-lg border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`} />
            <button type="button" onClick={handleTest} disabled={testing}
              className="px-5 py-2.5 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 font-medium">
              {testing ? 'Testing...' : 'Test'}
            </button>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${dk ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              {testResult.matched ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-emerald-500 text-sm font-bold">Pattern Matched:</span>
                    <span className={`text-sm ${dk ? 'text-white' : 'text-gray-800'}`}>{testResult.pattern_name}</span>
                  </div>

                  {/* Pattern blocks */}
                  <div className="flex gap-1 flex-wrap items-center mb-3">
                    {testResult.pattern_blocks.map((block, i) => (
                      <React.Fragment key={i}>
                        <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${blockColor(block)}`}>
                          [{block}]
                        </span>
                        {i < testResult.pattern_blocks.length - 1 && <span className={`text-xs ${dk ? 'text-gray-500' : 'text-gray-400'}`}>+</span>}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-2 mb-4">
                    {testResult.breakdown.map((b, i) => (
                      <div key={i} className={`p-2.5 rounded border ${dk ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${blockColor(b.type)}`}>{b.type}</span>
                          <span className={`text-sm ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{b.english}</span>
                          <span className={`font-mono text-sm ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{b.latin}</span>
                          <span className={`font-mono text-sm ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">{b.arabic}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Final Result */}
                  <div className={`p-3 rounded border-2 ${dk ? 'bg-gray-800 border-emerald-600' : 'bg-emerald-50 border-emerald-400'}`}>
                    <p className={`text-xs font-bold mb-1 ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>Final Result</p>
                    <p className={`font-mono text-sm mb-1 ${dk ? 'text-emerald-300' : 'text-emerald-800'}`}>{testResult.result.latin}</p>
                    <p className={`font-mono text-sm ${dk ? 'text-amber-300' : 'text-amber-800'}`} dir="rtl">{testResult.result.arabic}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p className={`text-sm font-bold ${dk ? 'text-amber-400' : 'text-amber-700'}`}>No Pattern Matched</p>
                  <p className={`text-xs mt-1 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
                    {testResult.message || 'Try creating a pattern that matches this sentence structure.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SentenceConstructionAdmin;
